import type { ReactElement } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import { useDispatch, useSelector } from "react-redux";

import Sentry from "@similarweb/sentry-logger";
import { CategoryChipItem } from "@similarweb/ui-components";

import { updateUniqueSegmentsIds } from "app/actions/segmentsModuleActions";
import categoryService from "app/common/services/categoryService";
import type { IFlattenedCategory, ICategory } from "app/common/services/categoryService.types";
import { swSettings } from "app/common/services/swSettings";
import {
    categoryIdChanged,
    dashboardWizardKeyChanged,
    dashboardWizardSetAvaliableSource,
    dashboardWizardWebSourceChanged,
    dashboardWizardWidgetTypeChanged,
} from "app/components/dashboard/widget-wizard/actions/dashboardWizardActionCreators";
import { selectDashboardWizard } from "app/components/dashboard/widget-wizard/selectors/dashboardSelectors";
import { usePrevious } from "app/components/hooks/usePrevious";
import type { ILegendItems } from "app/components/React/Legends/legendsTypes";
import type { TableRawData } from "app/pages/industry-analysis/players/market-shares/types";
import fetchService from "app/common/services/salesFetchService";
import { getCustomSegmentsMetadata } from "app/pages/segments/config/segmentsConfigHelpers";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import DurationService from "app/services/DurationService";

import { mergeSegmentIds, removeInitialSegmentsIds } from "../../segments/helpers";
import {
    selectAccountSegments,
    selectIsEditing,
    selectUniqueSegments,
    selectWidgetFilters,
} from "../../segments/selectors";
import IndustryItem from "../components/IndustryItem/IndustryItem";
import { TRAFFIC_SOURCE, defaultWidgetType, selectedDomainItemsInitialState } from "../constants";
import { createWebsitesItems, getDomainsArray, getIndustryItemText } from "../helpers";
import type { Domain } from "../types";

const useIndustries = (customDashboard) => {
    const dispatch = useDispatch();

    const widgetFilters = useSelector(selectWidgetFilters);
    const dashboardWizard = useSelector(selectDashboardWizard);
    const customSegmentsMeta = useSelector(selectAccountSegments);
    const uniqueSegments = useSelector(selectUniqueSegments) || {};
    const isEditing = useSelector(selectIsEditing);

    const [industriesItems, setIndustriesItems] = useState([]);
    const [selectedIndustry, setSelectedIndustry] = useState<ICategory>();
    const [selectedIndustryChip, setSelectedIndustryChip] = useState<ReactElement[]>([]);

    const [domainListItems, setDomainListItems] = useState<Domain[]>([]);

    const [selectedDomainItems, setSelectedDomainItems] = useState(selectedDomainItemsInitialState);

    const [isSegmentsLoading, setIsSegmentsLoading] = useState(false);

    const previousIndustry = usePrevious(selectedIndustry);

    const mergedIndustries = useMemo(() => {
        const customAndSharedCategories = UserCustomCategoryService.getCustomAndSharedCategories();
        const categories = categoryService.getFlattenedCategoriesList();

        return [...categories, ...customAndSharedCategories] as IFlattenedCategory[];
    }, []);

    useEffect(() => {
        if (!isEditing) {
            dispatch(dashboardWizardWidgetTypeChanged(customDashboard, defaultWidgetType));
        }
    }, []);

    const getCategoryDomains = async (category: string) => {
        try {
            const widget = dashboardWizard.widget;

            const durationObject = DurationService.getDurationData(widget.duration);
            const res: TableRawData = await fetchService.get("api/Market/Trends/Table", {
                ...widgetFilters,
                category,
                keys: category,
                from: durationObject.forAPI.from,
                to: durationObject.forAPI.to,
                country: widget.country,
                webSource: "Total",
            });

            return getDomainsArray(res.Data);
        } catch (error) {
            Sentry.error(`MarketAnalysis getCategoryDomains`, error);
        }
    };

    useEffect(() => {
        setIndustriesItems(getIndustriesItemsList(mergedIndustries));
    }, [widgetFilters.category, mergedIndustries, dashboardWizard.widget.key?.length]);

    useEffect(() => {
        if (!isEmpty(selectedIndustry) && !isEqual(previousIndustry, selectedIndustry)) {
            updateSelectedIndustriesList(selectedIndustry);

            setIndustryChip(selectedIndustry);
        }
    }, [selectedIndustry]);

    const getIndustriesItemsList = (industries: IFlattenedCategory[]): ReactElement[] => {
        const listItems = [];

        for (let i = 0; i < industries.length; i++) {
            const currentItem = industries[i];
            const text = getIndustryItemText(currentItem);
            const hasSegments = !!currentItem?.segments?.length;

            let mergedSegments = {};
            let isIndustryDisabled = false;

            if (text) {
                currentItem.text = text;
            }

            if (hasSegments) {
                const { SegmentsPerDashboardHardLimit } = swSettings?.current?.resources;

                mergedSegments = mergeSegmentIds(uniqueSegments, currentItem?.segments);

                const segmentsNumber = mergedSegments && Object.keys(mergedSegments).length;

                if (segmentsNumber > SegmentsPerDashboardHardLimit) {
                    isIndustryDisabled = true;
                }
            }

            if (
                currentItem.categoryId === widgetFilters.keys ||
                currentItem.forApi === widgetFilters.keys ||
                (currentItem.forUrl === widgetFilters.keys && !selectedIndustry)
            ) {
                setIndustry(currentItem);
            }

            listItems.push(
                <IndustryItem
                    item={currentItem}
                    onSelect={(industry) => onIndustrySelect(industry)}
                    isDisabled={isIndustryDisabled}
                />,
            );
        }

        return listItems;
    };

    const setIndustryChip = (industry: ICategory) => {
        if (industry) {
            setSelectedIndustryChip([
                <CategoryChipItem
                    key={industry.id}
                    text={industry.text}
                    onCloseItem={() => resetIndustriesWizard(industry)}
                />,
            ]);
        }
    };

    const onIndustrySelect = (industry: ICategory) => {
        if (industry?.segments?.length) {
            const mergedSegments = mergeSegmentIds(uniqueSegments, industry?.segments);

            dispatch(updateUniqueSegmentsIds(mergedSegments));
        }

        setIndustry(industry);
    };

    const setIndustry = async (industry: ICategory) => {
        if (!industry?.domains) {
            const domains = await getCategoryDomains(industry.forApi);

            industry.domains = domains;
        }

        dispatch(categoryIdChanged(dashboardWizard, industry.forUrl));

        setSelectedIndustry(industry);
    };

    const resetIndustriesWizard = (industry) => {
        if (industry?.segments?.length) {
            dispatch(updateUniqueSegmentsIds(removeInitialSegmentsIds(uniqueSegments, industry?.segments)));
        }

        setSelectedIndustryChip([]);
        setSelectedIndustry(undefined);
        setDomainListItems([]);

        setSelectedDomainItems(selectedDomainItemsInitialState);
        dispatch(categoryIdChanged(dashboardWizard, ""));

        dispatch(dashboardWizardKeyChanged(dashboardWizard, []));
    };

    const updateSelectedIndustriesList = async (industry: ICategory) => {
        if (industry?.domains || industry?.segments) {
            const keys: ILegendItems = dashboardWizard.widget.key;
            const industrySegments = industry?.segments;
            const hasSegments = !!industrySegments?.length;
            const widget = dashboardWizard.widget;
            const trafficSource = hasSegments ? TRAFFIC_SOURCE.DESKTOP : TRAFFIC_SOURCE.TOTAL;

            const customDashboardWithKey = cloneDeep(customDashboard);

            let segmentsMeta = customSegmentsMeta;

            const data = hasSegments ? [...industry.domains, ...industrySegments] : industry.domains;

            setIsSegmentsLoading(true);

            if (hasSegments && !segmentsMeta) {
                const { AccountSegments } = await getCustomSegmentsMetadata();

                segmentsMeta = AccountSegments;
            }

            const { domainsList, selectedDomains, selectedWebsites } = await createWebsitesItems(
                data,
                keys,
                segmentsMeta,
            );

            setIsSegmentsLoading(false);

            setSelectedDomainItems(selectedWebsites);
            setDomainListItems(domainsList);

            customDashboardWithKey.widget.key = selectedDomains;

            dispatch(dashboardWizardSetAvaliableSource(customDashboardWithKey, widget.country));
            dispatch(dashboardWizardWebSourceChanged(customDashboard, trafficSource));

            dispatch(dashboardWizardKeyChanged(dashboardWizard, selectedDomains));
        }
    };

    const getIndustriesItems = async (query: string) =>
        industriesItems.filter((item) => item.props.item.text.toLowerCase().indexOf(query.toLowerCase()) > -1);

    const websitesSearch = useCallback((searchText: string, item: ReactElement<{ item: { domain?: string } }>) => {
        const domain = item?.props?.item?.domain;

        if (domain) {
            return domain.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
        }

        return false;
    }, []);

    const onDomainSelect = (domain: Domain) => {
        const result = cloneDeep(selectedDomainItems);
        const domains = cloneDeep(domainListItems);

        let updatedKeys = [];

        if (domain.visible) {
            delete result.ids[domain.id];
            result.size = result.size - 1;

            updatedKeys = domains.map((key) => {
                if (key.id === domain.id) {
                    return {
                        ...key,
                        visible: false,
                    };
                }

                return key;
            });
        } else {
            updatedKeys = domains.map((key) => {
                if (key.id === domain.id) {
                    return {
                        ...key,
                        visible: true,
                    };
                }

                return key;
            });

            result.ids[domain.id] = true;
            result.size = result.size + 1;
        }

        setSelectedDomainItems(result);
        setDomainListItems(updatedKeys);

        dispatch(dashboardWizardKeyChanged(dashboardWizard, updatedKeys));
    };

    return {
        industriesItems,
        selectedIndustry,
        websitesSearch,
        setSelectedIndustry,
        selectedIndustryChip,
        getIndustriesItems,
        selectedDomainItems,
        setSelectedDomainItems,
        onDomainSelect,
        domainListItems,
        setDomainListItems,
        isSegmentsLoading,
    };
};

export default useIndustries;
